import { useCallback, useRef } from "react";

/**
 * Wraps a Wage Settings save so a second click cannot send a second request while the first is in flight.
 *
 * Disabling Save on `isPending` is not enough on its own: TanStack notifies observers on a later tick, so
 * a fast double-click can land before the button re-renders disabled. Every save queues a
 * department-wide wage recalculation, so the guard is a ref checked synchronously.
 *
 * `send` gets a `release` callback and must call it once the request settles, success or error.
 */
export const useSingleFlightSave = <TBody>(send: (body: TBody, release: () => void) => void) => {
	const inFlight = useRef(false);
	return useCallback(
		(body: TBody) => {
			if (inFlight.current) return;
			inFlight.current = true;
			try {
				send(body, () => {
					inFlight.current = false;
				});
			} catch (error) {
				// A synchronous throw never reaches `release`, which would lock this section's Save until remount.
				inFlight.current = false;
				throw error;
			}
		},
		[send],
	);
};
